import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useUser } from '@clerk/clerk-react';
import { usersApi } from '../lib/permissions';
import type {
  PortalUserFull,
  EffectivePermissions,
  PermissionContextState,
  RoleName,
  SectionName,
} from '../types/permissions';

const PermissionContext = createContext<PermissionContextState | undefined>(undefined);

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded, isSignedIn } = useUser();
  const [portalUser, setPortalUser] = useState<PortalUserFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = useCallback(async () => {
    if (!isLoaded) return;

    if (!isSignedIn || !user) {
      setPortalUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Sync user from Clerk to portal_users
      await usersApi.syncFromClerk(
        user.id,
        user.primaryEmailAddress?.emailAddress || '',
        user.firstName,
        user.lastName
      );

      // Get full user with permissions
      const fullUser = await usersApi.getFullByClerkId(user.id);
      setPortalUser(fullUser);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const permissions = useMemo<EffectivePermissions | null>(() => {
    if (!portalUser) return null;

    const roles = portalUser.roles || [];
    const highestRoleLevel = portalUser.highest_role_level;
    const customerAccess = portalUser.customer_access || [];

    const isSuperuser = roles.some(r => r.name === 'superuser');
    const isAdministrator = roles.some(r => r.name === 'administrator');
    const isCustomerManager = roles.some(r => r.name === 'customer_manager');
    const isUser = roles.some(r => r.name === 'user');

    const highestRole: RoleName | null = roles.length > 0
      ? roles.reduce((prev, curr) =>
          curr.hierarchy_level < prev.hierarchy_level ? curr : prev
        ).name as RoleName
      : null;

    const accessibleCustomerIds = customerAccess
      .filter(a => a.can_view)
      .map(a => a.customer_id);

    const hasAccessToCustomer = (customerId: string): boolean => {
      if (isSuperuser) return true;
      return customerAccess.some(a => a.customer_id === customerId);
    };

    const canViewCustomer = (customerId: string): boolean => {
      if (isSuperuser) return true;
      const access = customerAccess.find(a => a.customer_id === customerId);
      return access?.can_view ?? false;
    };

    const canEditCustomer = (customerId: string): boolean => {
      if (isSuperuser || isAdministrator) {
        // Admins with access to a customer have full edit rights
        if (isSuperuser) return true;
        return hasAccessToCustomer(customerId);
      }
      const access = customerAccess.find(a => a.customer_id === customerId);
      return access?.can_edit ?? false;
    };

    const canDeleteCustomer = (customerId: string): boolean => {
      if (isSuperuser) return true;
      if (isAdministrator) {
        return hasAccessToCustomer(customerId);
      }
      const access = customerAccess.find(a => a.customer_id === customerId);
      return access?.can_delete ?? false;
    };

    const canViewSection = (customerId: string, section: SectionName): boolean => {
      if (isSuperuser) return true;
      if (isAdministrator && hasAccessToCustomer(customerId)) return true;

      const access = customerAccess.find(a => a.customer_id === customerId);
      if (!access?.can_view) return false;

      const sectionPerm = access.section_permissions?.find(sp => sp.section_name === section);
      // Default to true if no specific section permission exists
      return sectionPerm?.can_view ?? true;
    };

    const canEditSection = (customerId: string, section: SectionName): boolean => {
      if (isSuperuser) return true;
      if (isAdministrator && hasAccessToCustomer(customerId)) return true;

      const access = customerAccess.find(a => a.customer_id === customerId);
      if (!access?.can_edit) return false;

      const sectionPerm = access.section_permissions?.find(sp => sp.section_name === section);
      // Default to the customer-level edit permission if no specific section permission exists
      return sectionPerm?.can_edit ?? access.can_edit;
    };

    return {
      isSuperuser,
      isAdministrator,
      isCustomerManager,
      isUser,
      highestRole,
      highestRoleLevel,
      hasAccessToCustomer,
      canViewCustomer,
      canEditCustomer,
      canDeleteCustomer,
      canViewSection,
      canEditSection,
      accessibleCustomerIds,
    };
  }, [portalUser]);

  const value: PermissionContextState = {
    portalUser,
    loading,
    error,
    permissions,
    refreshPermissions: loadPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextState {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Convenience hooks
export function useCanView(customerId: string, section?: SectionName): boolean {
  const { permissions } = usePermissions();
  if (!permissions) return false;

  if (section) {
    return permissions.canViewSection(customerId, section);
  }
  return permissions.canViewCustomer(customerId);
}

export function useCanEdit(customerId: string, section?: SectionName): boolean {
  const { permissions } = usePermissions();
  if (!permissions) return false;

  if (section) {
    return permissions.canEditSection(customerId, section);
  }
  return permissions.canEditCustomer(customerId);
}

export function useCanDelete(customerId: string): boolean {
  const { permissions } = usePermissions();
  if (!permissions) return false;
  return permissions.canDeleteCustomer(customerId);
}

export function useIsSuperuser(): boolean {
  const { permissions } = usePermissions();
  return permissions?.isSuperuser ?? false;
}

export function useIsAdmin(): boolean {
  const { permissions } = usePermissions();
  return (permissions?.isSuperuser || permissions?.isAdministrator) ?? false;
}

export function useHasAnyRole(): boolean {
  const { permissions } = usePermissions();
  return permissions?.highestRole !== null;
}

export function useHasAccess(): boolean {
  const { permissions } = usePermissions();
  if (!permissions) return false;
  return permissions.isSuperuser || permissions.accessibleCustomerIds.length > 0;
}
